import type {
    CompStructure,
    DeserializationMappers,
    RefInfo,
    SerializedCompStructure
} from '@wix/document-services-types'
import type {CreateExtArgs, ExtensionAPI} from '@wix/document-manager-core'
import {addIdToMap, getCustomId, getIdentifierForQuery} from '../utils'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, DATA_TYPES, DATA_TYPES_VALUES_WITH_HASH} from '../../../../constants/constants'
import * as _ from 'lodash'
import {SERIALIZED_DATA_KEYS, SERIALIZED_SCOPED_DATA_KEYS} from '../../constants'
import {replaceVariantsOnSerialized} from '../../../variants/variantsReplacement'
import type {DataModelExtensionAPI} from '../../../dataModel/dataModel'
import type {DataExtensionAPI} from '../../../data'
import {addNamespaceToStructure} from '../componentDeserializationStructure'
import type {SchemaExtensionAPI} from '../../../schema/schema'

export const setCompQueryValue = (compStructure: CompStructure, id: string, itemType: string) => {
    const compDataQueryKey = COMP_DATA_QUERY_KEYS_WITH_STYLE[itemType]
    compStructure[compDataQueryKey] = DATA_TYPES_VALUES_WITH_HASH[itemType] ? `#${id}` : id
}

const createAndAddRefArray = (
    extensionAPI: ExtensionAPI,
    refValues: string[],
    itemType: string,
    pageId: string
): string => {
    const {data: dataApi} = extensionAPI as DataExtensionAPI
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const refArray = dataApi.refArray.create(refValues)
    return dataModel.addDeserializedItem(refArray, itemType, pageId).id
}

export const linkRefArrayOrNonScopedValue = (
    extensionAPI: ExtensionAPI,
    compStructure: CompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers,
    refValues: string[]
) => {
    const {oldToNewIdMap} = mappers ?? {}
    const isThereRefArrayOrStyleObjectToLink = !_.isEmpty(refValues)
    if (isThereRefArrayOrStyleObjectToLink) {
        const scopedSerializeKey = SERIALIZED_SCOPED_DATA_KEYS[itemType]
        const shouldLinkNonScopedValue =
            !compStructure[scopedSerializeKey] && [DATA_TYPES.theme, DATA_TYPES.design].includes(itemType)

        const dataQuery = shouldLinkNonScopedValue
            ? refValues[0]
            : createAndAddRefArray(extensionAPI, refValues, itemType, pageId)

        if (dataQuery) {
            addIdToMap({id: getIdentifierForQuery(extensionAPI, compStructure, itemType)}, dataQuery, oldToNewIdMap)
            setCompQueryValue(compStructure, dataQuery, itemType)
        }
    }
}

const updateVariantRelation = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers,
    scopedValue: any,
    variants: string,
    stylesPerPage?: boolean
) => {
    const {extensionAPI} = createExtArgs

    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {data} = extensionAPI as DataExtensionAPI
    const {oldToNewIdMap} = mappers ?? {}
    const updatedVariants = variants.split(',').map(oldVariant => oldToNewIdMap[oldVariant])
    const newVariants = updatedVariants.filter(Boolean)

    if (newVariants.length !== updatedVariants.length) {
        return
    }

    const scopedValueId = addNamespaceToStructure(
        {createExtArgs, compStructure, pageId},
        scopedValue,
        itemType,
        oldToNewIdMap,
        stylesPerPage
    )
    if (compStructure.id && scopedValueId) {
        const variantRelation = data.variantRelation.create(newVariants, compStructure.id!, scopedValueId!)
        return dataModel.addItem(variantRelation, itemType, pageId).id
    }
}

export const updateScopedValues = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers,
    refValues: string[] = [],
    path = SERIALIZED_SCOPED_DATA_KEYS[itemType],
    stylesPerPage?: boolean
) => {
    const scopedValues = _.get(compStructure, path)
    if (scopedValues) {
        const relationsIds = _.map(scopedValues, (scopedValue, variant: string) => {
            return updateVariantRelation(
                createExtArgs,
                compStructure,
                itemType,
                pageId,
                mappers,
                scopedValue,
                variant,
                stylesPerPage
            )
        })

        refValues.splice(refValues.length, 0, ..._.compact(relationsIds))
    }
}

export const updateNonScopedValue = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers,
    refValues: string[] = [],
    path = SERIALIZED_DATA_KEYS[itemType],
    stylesPerPage?: boolean
) => {
    const nonScopedValue = _.get(compStructure, path)
    const {oldToNewIdMap} = mappers ?? {}
    if (nonScopedValue) {
        const nonScopedId = addNamespaceToStructure(
            {
                createExtArgs,
                compStructure,
                pageId
            },
            nonScopedValue,
            itemType,
            oldToNewIdMap,
            stylesPerPage
        )
        if (nonScopedId) {
            refValues.unshift(nonScopedId)
        }
    }
}

export const fixRefArrayValues = (
    compStructure: CompStructure,
    itemType: string,
    mappers: DeserializationMappers = {oldToNewIdMap: {}},
    nonScopedPath = SERIALIZED_DATA_KEYS[itemType],
    scopedPath = SERIALIZED_SCOPED_DATA_KEYS[itemType]
) => {
    const {variantMapper} = mappers

    if (!Array.isArray(variantMapper) || variantMapper.length === 0) {
        return
    }

    const refArrayValues = {
        ..._.get(compStructure, scopedPath),
        '': _.get(compStructure, nonScopedPath)
    }

    const {defaultValue, scopedValues} = replaceVariantsOnSerialized(refArrayValues, variantMapper)

    if (Object.keys(scopedValues).length > 0) {
        _.set(compStructure, scopedPath, scopedValues)
    } else {
        _.unset(compStructure, scopedPath)
    }

    if (defaultValue) {
        _.set(compStructure, nonScopedPath, defaultValue)
    }
}

export const updateComponentScopedValues = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers = {oldToNewIdMap: {}},
    stylesPerPage?: boolean
) => {
    const {extensionAPI} = createExtArgs

    const refValues: string[] = []
    const {oldToNewIdMap} = mappers ?? {}
    const dataQueryId = getCustomId(oldToNewIdMap, {
        id: getIdentifierForQuery(extensionAPI, compStructure, itemType)
    })

    if (dataQueryId) {
        setCompQueryValue(compStructure, dataQueryId, itemType)
        return
    }
    fixRefArrayValues(compStructure, itemType, mappers)
    updateNonScopedValue(
        createExtArgs,
        compStructure,
        itemType,
        pageId,
        mappers,
        refValues,
        SERIALIZED_DATA_KEYS[itemType],
        stylesPerPage
    )
    updateScopedValues(
        createExtArgs,
        compStructure,
        itemType,
        pageId,
        mappers,
        refValues,
        SERIALIZED_SCOPED_DATA_KEYS[itemType],
        stylesPerPage
    )
    linkRefArrayOrNonScopedValue(extensionAPI, compStructure, itemType, pageId, mappers, refValues)
}

const updateComponentNonRootScopedValuesRecursively = (
    createExtArgs: CreateExtArgs,
    item: any,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers
) => {
    const {extensionAPI} = createExtArgs
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const referenceFieldsInfo = item.type && schemaAPI.extractReferenceFieldsInfo(itemType, item.type)
    if (!referenceFieldsInfo?.length) return
    for (const info of referenceFieldsInfo) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        updateFieldNonRootScopedValues(createExtArgs, item, info, itemType, pageId, mappers)
    }
}

const updateFieldNonRootScopedValues = (
    createExtArgs: CreateExtArgs,
    item: any,
    info: RefInfo,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers
) => {
    const {isRelationalSplit, path} = info
    const field = _.get(item, path)
    if (!_.isObject(field) && !_.isArray(field)) return

    if (isRelationalSplit) {
        const refValues: string[] = []

        fixRefArrayValues(item, itemType, mappers, [...path, 'default'], [...path, 'scoped'])
        updateNonScopedValue(createExtArgs, item, itemType, pageId, mappers, refValues, [...path, 'default'])
        updateScopedValues(createExtArgs, item, itemType, pageId, mappers, refValues, [...path, 'scoped'])

        const {extensionAPI} = createExtArgs
        const refArrayId = createAndAddRefArray(extensionAPI, refValues, itemType, pageId)
        _.set(item, path, `#${refArrayId}`)
    } else {
        _.forEach(field, childField =>
            updateComponentNonRootScopedValuesRecursively(createExtArgs, childField, itemType, pageId, mappers)
        )
    }
}

export const updateComponentNonRootScopedValues = (
    createExtArgs: CreateExtArgs,
    compStructure: SerializedCompStructure,
    itemType: string,
    pageId: string,
    mappers: DeserializationMappers = {oldToNewIdMap: {}}
) => {
    const item = compStructure[itemType]
    if (!_.isObject(item)) return

    updateComponentNonRootScopedValuesRecursively(createExtArgs, item, itemType, pageId, mappers)
}
